import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, FolderSimple, ArrowRight, Globe } from "@phosphor-icons/react";

const STAT_DEFS = [
  { key: "total_projects", label: "Projects" },
  { key: "live", label: "Live" },
  { key: "ready", label: "Ready to deploy" },
  { key: "needs_attention", label: "Needs attention" },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [overview, setOverview] = useState({});

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
    api.get("/overview").then((r) => setOverview(r.data)).catch(() => {});
  }, []);

  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-6 py-10 sm:px-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-4xl font-bold tracking-tight">Your apps</h1>
            <p className="mt-2 text-muted-foreground">Upload, prepare and go live — no commands required.</p>
          </div>
          <Button onClick={() => navigate("/new")} data-testid="new-project-button" className="rounded-full active:scale-95">
            <Plus size={18} weight="bold" /> New project
          </Button>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
          {STAT_DEFS.map((s, i) => (
            <motion.div key={s.key} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
              className="rounded-2xl border border-border bg-card p-6" data-testid={`stat-${s.key}`}>
              <p className="text-3xl font-bold font-display">{overview[s.key] ?? 0}</p>
              <p className="mt-1 text-sm text-muted-foreground">{s.label}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-10">
          {projects === null ? (
            <div className="grid gap-4 md:grid-cols-2">
              {[1, 2].map((i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}
            </div>
          ) : projects.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-border bg-card p-16 text-center" data-testid="empty-state">
              <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-secondary">
                <FolderSimple size={32} weight="duotone" />
              </div>
              <h3 className="mt-6 font-display text-2xl font-medium">No apps yet</h3>
              <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">Upload your first project ZIP and we'll get it ready to go live.</p>
              <Button onClick={() => navigate("/new")} className="mt-6 rounded-full active:scale-95" data-testid="empty-new-project">
                <Plus size={18} weight="bold" /> Upload a project
              </Button>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {projects.map((p, i) => (
                <motion.button key={p.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                  onClick={() => navigate(p.status === "live" ? `/projects/${p.id}` : `/projects/${p.id}/flow`)}
                  data-testid={`project-card-${p.id}`}
                  className="group flex items-center justify-between rounded-2xl border border-border bg-card p-6 text-left transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3">
                      <h3 className="truncate font-display text-xl font-medium">{p.name}</h3>
                      <StatusBadge status={p.status} />
                    </div>
                    {p.deployment?.url ? (
                      <p className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground font-mono">
                        <Globe size={14} /> {p.deployment.domain}
                      </p>
                    ) : (
                      <p className="mt-2 text-sm text-muted-foreground">
                        {p.analysis?.project_type || "Uploaded project"}
                      </p>
                    )}
                  </div>
                  <ArrowRight size={20} className="shrink-0 text-muted-foreground transition-transform duration-200 group-hover:translate-x-1" />
                </motion.button>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
